/**
 * Mock Interview page — timed practice under pressure.
 *
 * Flow:
 * 1. Setup: pick company, role, level, time limit
 * 2. Random question loads → timer starts
 * 3. User writes answer while countdown runs
 * 4. On submit or time expiry → answer is saved + evaluated
 * 5. Navigate to evaluation results
 *
 * The timer runs entirely on the frontend. The backend only tracks
 * the session metadata (time_limit, time_used, completed).
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import TimerIcon from "@mui/icons-material/Timer";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SendIcon from "@mui/icons-material/Send";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";

import {
  listCompanies,
  getRandomQuestion,
  startMockSession,
  completeMockSession,
  createAnswer,
  getAnswer,
  createEvaluation,
  Question,
} from "../api/client";
import UpgradePrompt, { isTierLimitError } from "../components/UpgradePrompt";
import VoiceInput from "../components/VoiceInput";
import { toUserSafeErrorMessage } from "../utils/errorMessage";

const ROLES = ["MLE", "PM", "TPM", "EM"];
const LEVELS = [
  { value: "entry", labelKey: "MockInterview.levelEntryLabel" },
  { value: "mid", labelKey: "MockInterview.levelMidLabel" },
  { value: "senior", labelKey: "MockInterview.levelSeniorLabel" },
  { value: "staff", labelKey: "MockInterview.levelStaffLabel" },
];
const TIME_OPTIONS = [
  { value: 120, labelKey: "MockInterview.time2MinLabel" },
  { value: 180, labelKey: "MockInterview.time3MinLabel" },
  { value: 300, labelKey: "MockInterview.time5MinLabel" },
  { value: 600, labelKey: "MockInterview.time10MinLabel" },
];

type Phase = "setup" | "active" | "submitting" | "timeup";

export default function MockInterview() {
  const { t } = useTranslation("pages-products");
  const navigate = useNavigate();
  const location = useLocation();
  const passedQuestionId = (location.state as any)?.questionId || "";
  const passedQuestionText = (location.state as any)?.questionText || "";

  // Setup state
  const [companyId, setCompanyId] = useState("");
  const [role, setRole] = useState("");
  const [level, setLevel] = useState("");
  const [timeLimit, setTimeLimit] = useState(300);

  // Active interview state
  const [phase, setPhase] = useState<Phase>("setup");
  const [question, setQuestion] = useState<Question | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [tierLimitInfo, setTierLimitInfo] = useState<{
    currentUsage?: number;
    limit?: number;
  }>({});

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  // Idempotency: once a step succeeds we store its id so a retry after a
  // later-step failure reuses it instead of double-creating (which would
  // burn the monthly eval quota and orphan the session).
  const answerIdRef = useRef<string | null>(null);
  const versionIdRef = useRef<string | null>(null);
  const evaluationIdRef = useRef<string | null>(null);
  // Guards the timer-expiry auto-submit so it dispatches AT MOST ONCE. Without
  // this, a tier-limit error that sets phase back to "active" while secondsLeft
  // is still 0 would re-fire the auto-submit effect in a loop.
  const autoSubmittedRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const [audioMuted, setAudioMuted] = useState(false);

  /** Play a beep via Web Audio API — zero dependencies, works in all browsers. */
  const playBeep = useCallback(
    (frequency: number, durationMs = 120) => {
      if (audioMuted) return;
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new AudioContext();
        }
        const ctx = audioCtxRef.current;
        if (ctx.state === "suspended") ctx.resume();

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.frequency.value = frequency;
        osc.type = "sine";
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(
          0.001,
          ctx.currentTime + durationMs / 1000
        );

        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + durationMs / 1000);
      } catch {
        // Audio not supported — silently degrade
      }
    },
    [audioMuted]
  );

  const { data: companies } = useQuery({
    queryKey: ["companies"],
    queryFn: listCompanies,
  });

  const selectedCompany = companies?.find((c) => c.id === companyId);

  // Timer logic
  useEffect(() => {
    if (phase !== "active") return;

    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          // Time's up — auto-submit
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [phase]);

  // Audio alerts at key time thresholds
  useEffect(() => {
    if (phase !== "active") return;
    if (secondsLeft === 60) playBeep(440);        // 1min — gentle reminder
    else if (secondsLeft === 30) playBeep(660);    // 30s — urgent
    else if (secondsLeft === 10) playBeep(880);    // 10s — alarming
    else if (secondsLeft <= 5 && secondsLeft > 0) playBeep(880, 80); // final countdown
  }, [secondsLeft, phase, playBeep]);

  const handleStart = async () => {
    setError("");
    // Fresh interview — clear the one-shot auto-submit guard.
    autoSubmittedRef.current = false;
    try {
      // Use the passed question if available, otherwise get a random one
      let q: Question;
      if (passedQuestionId) {
        q = {
          id: passedQuestionId,
          question_text: passedQuestionText,
          role_tags: [],
          company_tags: [],
          competency_tags: [],
          difficulty: "",
          level_band: null,
          source: "curated",
          usage_count: 0,
          track: "standard",
          interview_type: "behavioral",
          tags: [],
          ideal_answer_points: [],
        };
      } else {
        q = await getRandomQuestion({
          role: role || undefined,
          level: level || undefined,
        });
      }
      setQuestion(q);

      // Create mock session on the backend
      const session = await startMockSession({
        question_id: q.id,
        time_limit_seconds: timeLimit,
      });
      setSessionId(session.id);

      // Start the clock
      setSecondsLeft(timeLimit);
      startTimeRef.current = Date.now();
      setPhase("active");
    } catch (err: any) {
      setError(toUserSafeErrorMessage(err, t("MockInterview.failedToStartError")));
    }
  };

  const handleSubmit = useCallback(async () => {
    if (phase === "submitting" || !sessionId || !question) return;
    setPhase("submitting");

    const timeUsed = Math.round((Date.now() - startTimeRef.current) / 1000);

    try {
      // Step 1 — Save the answer (POST returns metadata only, no versions).
      // Reuse the previously created answer on retry so we never double-create.
      if (!answerIdRef.current) {
        const created = await createAnswer({
          question_id: question.id,
          target_company_id: companyId,
          target_role: role,
          experience_level: level,
          answer_text: answerText,
        });
        answerIdRef.current = created.id;
      }

      // Step 2 — Fetch the full answer with versions to get the version ID.
      if (!versionIdRef.current) {
        const answer = await getAnswer(answerIdRef.current);
        const versionId =
          answer.versions && answer.versions.length > 0
            ? answer.versions[0].id
            : null;

        if (!versionId) {
          setError(t("MockInterview.failedToRetrieveVersionError"));
          setPhase("active");
          return;
        }
        versionIdRef.current = versionId;
      }

      // Step 3 — Kick off evaluation (the quota-consuming step). Reuse the
      // existing evaluation on retry so a later failure can't burn quota twice.
      if (!evaluationIdRef.current) {
        const evaluation = await createEvaluation(versionIdRef.current);
        evaluationIdRef.current = evaluation.id;
      }

      // Step 4 — Complete the mock session. If only this step previously
      // failed, the evaluation already exists and we resume right here.
      await completeMockSession(sessionId, {
        time_used_seconds: timeUsed,
        answer_version_id: versionIdRef.current,
        evaluation_id: evaluationIdRef.current,
      });

      // Navigate to results
      navigate(`/evaluations/${evaluationIdRef.current}`);
    } catch (err: any) {
      const tierCheck = isTierLimitError(err);
      if (tierCheck.isLimit) {
        setTierLimitInfo({
          currentUsage: tierCheck.currentUsage,
          limit: tierCheck.limit,
        });
        setShowUpgrade(true);
        // Restore the editable state so the user's typed answer isn't trapped
        // behind a frozen "Submitting…" UI after dismissing the dialog.
        setPhase("active");
        return;
      }
      // The evaluation already exists (only session-completion failed) — the
      // user's answer was evaluated and quota already spent, so let them reach
      // the result rather than stranding them on a failing retry.
      if (evaluationIdRef.current) {
        navigate(`/evaluations/${evaluationIdRef.current}`);
        return;
      }
      setError(toUserSafeErrorMessage(err, t("MockInterview.failedToSubmitError")));
      setPhase("active");
    }
    // answerIdRef/versionIdRef/evaluationIdRef are intentionally omitted: refs
    // have stable identity and `.current` is read at call time, so listing them
    // would do nothing but mislead. Do not "fix" the lint warning by adding them.
  }, [phase, sessionId, question, answerText, companyId, role, level, navigate, t]);

  // Auto-submit when timer hits 0. If the answer is long enough, evaluate it;
  // otherwise transition to an explicit "Time's up" state instead of stranding
  // the user on a frozen 0:00 with a disabled, un-submittable flow.
  useEffect(() => {
    if (phase !== "active" || secondsLeft !== 0) return;
    // Dispatch the timer-expiry handler at most once. A tier-limit error during
    // the auto-submit sets phase back to "active" while secondsLeft is still 0;
    // without this guard the effect would re-fire and loop the submit.
    if (autoSubmittedRef.current) return;
    if (answerText.trim().length >= 10) {
      autoSubmittedRef.current = true;
      handleSubmit();
    } else {
      autoSubmittedRef.current = true;
      setPhase("timeup");
      // Mark the session complete (abandoned) so it isn't left dangling.
      if (sessionId) {
        const timeUsed = Math.round(
          (Date.now() - startTimeRef.current) / 1000
        );
        completeMockSession(sessionId, { time_used_seconds: timeUsed }).catch(
          () => {
            // Best-effort cleanup — the user already has a path forward.
          }
        );
      }
    }
  }, [secondsLeft, phase, answerText, sessionId, handleSubmit]);

  // Keyboard shortcuts: Ctrl+Enter to submit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        if (phase === "active" && answerText.trim().length >= 10) {
          e.preventDefault();
          handleSubmit();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [phase, answerText, handleSubmit]);

  // Format seconds as MM:SS
  const formatTime = (s: number) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Timer color based on urgency
  const timerColor =
    secondsLeft <= 30 ? "error" : secondsLeft <= 60 ? "warning" : "primary";

  const timerPct = timeLimit > 0 ? (secondsLeft / timeLimit) * 100 : 0;

  const wordCount = answerText.trim().split(/\s+/).filter(Boolean).length;

  // ----- Setup phase -----
  if (phase === "setup") {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto" }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
        >
          <TimerIcon sx={{ mr: 1, verticalAlign: "bottom" }} />
          {t("MockInterview.pageTitle")}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t("MockInterview.setupIntroPart1")}{" "}
          {passedQuestionText
            ? t("MockInterview.setupIntroSelectedQuestion")
            : t("MockInterview.setupIntroRandomQuestion")}{" "}
          {t("MockInterview.setupIntroPart2")}
        </Typography>

        {passedQuestionText && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Trans
              i18nKey="MockInterview.selectedQuestionLabel"
              t={t}
              values={{ question: passedQuestionText }}
              components={[<strong key="strong0" />]}
            />
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={3}>
              {/* Company */}
              <FormControl fullWidth>
                <InputLabel>{t("MockInterview.targetCompanyLabel")}</InputLabel>
                <Select
                  value={companyId}
                  label={t("MockInterview.targetCompanyLabel")}
                  onChange={(e) => setCompanyId(e.target.value)}
                >
                  {companies?.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Role */}
              <FormControl fullWidth>
                <InputLabel>{t("MockInterview.targetRoleLabel")}</InputLabel>
                <Select
                  value={role}
                  label={t("MockInterview.targetRoleLabel")}
                  onChange={(e) => setRole(e.target.value)}
                >
                  {ROLES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Level */}
              <FormControl fullWidth>
                <InputLabel>{t("MockInterview.experienceLevelLabel")}</InputLabel>
                <Select
                  value={level}
                  label={t("MockInterview.experienceLevelLabel")}
                  onChange={(e) => setLevel(e.target.value)}
                >
                  {LEVELS.map((l) => (
                    <MenuItem key={l.value} value={l.value}>
                      {t(l.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Time Limit */}
              <FormControl fullWidth>
                <InputLabel>{t("MockInterview.timeLimitLabel")}</InputLabel>
                <Select
                  value={timeLimit}
                  label={t("MockInterview.timeLimitLabel")}
                  onChange={(e) => setTimeLimit(Number(e.target.value))}
                >
                  {TIME_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                onClick={handleStart}
                disabled={!companyId || !role || !level}
              >
                {t("MockInterview.startMockInterviewButton")}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ----- Time's up (answer too short to evaluate) phase -----
  if (phase === "timeup") {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto" }}>
        <Card>
          <CardContent>
            <Stack spacing={3} sx={{ textAlign: "center", py: 2 }}>
              <TimerIcon color="error" sx={{ fontSize: 48, mx: "auto" }} />
              <Typography variant="h5" fontWeight={500}>
                {t("MockInterview.timesUpTitle")}
              </Typography>
              <Typography color="text.secondary">
                {t("MockInterview.timesUpDescription")}
              </Typography>
              {question && (
                <Alert severity="info" sx={{ textAlign: "left" }}>
                  <Trans
                    i18nKey="MockInterview.questionLabel"
                    t={t}
                    values={{ question: question.question_text }}
                    components={[<strong key="strong0" />]}
                  />
                </Alert>
              )}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => {
                    // Reset back to a fresh setup so the user can try again.
                    setQuestion(null);
                    setSessionId(null);
                    setAnswerText("");
                    setSecondsLeft(0);
                    setError("");
                    answerIdRef.current = null;
                    versionIdRef.current = null;
                    evaluationIdRef.current = null;
                    autoSubmittedRef.current = false;
                    setPhase("setup");
                  }}
                >
                  {t("MockInterview.tryAnotherButton")}
                </Button>
                <Button variant="outlined" onClick={() => navigate("/coach")}>
                  {t("MockInterview.backToCoachButton")}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ----- Active / Submitting phase -----
  return (
    <Box sx={{ maxWidth: 800, mx: "auto" }}>
      {/* Timer bar */}
      <Card sx={{ mb: 2, bgcolor: secondsLeft <= 30 ? "error.light" : undefined }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <TimerIcon color={timerColor as any} />
            <Typography
              variant="h4"
              fontWeight={500}
              color={`${timerColor}.main`}
              sx={{ fontFamily: "monospace" }}
            >
              {formatTime(secondsLeft)}
            </Typography>
            <Tooltip title={audioMuted ? t("MockInterview.unmuteAlertsTooltip") : t("MockInterview.muteAlertsTooltip")}>
              <IconButton
                size="small"
                onClick={() => setAudioMuted(!audioMuted)}
                color={audioMuted ? "default" : "primary"}
                aria-label={audioMuted ? t("MockInterview.unmuteTimerAlertsAriaLabel") : t("MockInterview.muteTimerAlertsAriaLabel")}
                aria-pressed={audioMuted}
              >
                {audioMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
            </Tooltip>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ ml: "auto", display: { xs: "none", sm: "block" } }}
            >
              {selectedCompany?.name} &middot; {role} &middot; {level}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={timerPct}
            color={timerColor as any}
          />
        </CardContent>
      </Card>

      {/* Question */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            {t("MockInterview.yourQuestionLabel")}
          </Typography>
          <Typography variant="h6">{question?.question_text}</Typography>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Answer area */}
      <TextField
        fullWidth
        multiline
        minRows={10}
        maxRows={20}
        placeholder={t("MockInterview.answerPlaceholder")}
        value={answerText}
        onChange={(e) => setAnswerText(e.target.value)}
        disabled={phase === "submitting"}
        autoFocus
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <VoiceInput
          onTranscript={(text) =>
            setAnswerText((prev) =>
              prev ? prev.trimEnd() + " " + text : text
            )
          }
          disabled={phase === "submitting"}
        />
        <Typography variant="body2" color="text.secondary">
          {t("MockInterview.wordCountLabel", { count: wordCount })}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={t("MockInterview.ctrlEnterToSubmitHint")}>
          <span>
            <Button
              variant="contained"
              endIcon={
                phase === "submitting" ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              onClick={handleSubmit}
              disabled={phase === "submitting" || answerText.trim().length < 10}
            >
              {phase === "submitting" ? t("MockInterview.submittingButton") : t("MockInterview.submitAndEvaluateButton")}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Shortcut hints */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block", textAlign: "right" }}
      >
        {t("MockInterview.ctrlEnterToSubmitHint")}
      </Typography>

      {/* Upgrade prompt when tier limit is reached */}
      <UpgradePrompt
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        currentUsage={tierLimitInfo.currentUsage}
        limit={tierLimitInfo.limit}
      />
    </Box>
  );
}
